/**
 * @file Contact.jsx
 * @description Manages new user setup and profile creation
 * 
 * @section Responsibilities
 * - Collect user information
 * - Handle document uploads
 * - Validate profile data
 * - Track onboarding progress
 * 
 * @section Dependencies
 * - Profile Management: User data handling
 * - Document Service: File handling
 * - Validation Service: Data validation
 * 
 * @section Integration Points
 * - Profile API: Data submission
 * - Document Service: File handling
 * - Onboarding Flow: Progress tracking
 * 
 * @section State Management
 * - profileData: User information
 * - validationState: Form validation
 * - progress: Onboarding progress
 * 
 * @section Events
 * - onProfileUpdate: Profile changes
 * - onDocumentUpload: File handling
 * - onValidation: Form validation
 * 
 * @section Props
 * @prop {Object} initialData - Initial profile data
 * @prop {Function} onComplete - Completion callback
 */

import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import TextInput from "../../../../components/forms/TextInput";
import SelectInput from "../../../../components/forms/SelectInput";

function Contact({ sendData, submitted, backendFieldErrors = {} }) {
  const statesArray = [
    { text: "AL", value: "AL" },
    { text: "AK", value: "AK" },
    { text: "AZ", value: "AZ" },
    { text: "AR", value: "AR" },
    { text: "AS", value: "AS" },
    { text: "CA", value: "CA" },
    { text: "CO", value: "CO" },
    { text: "CT", value: "CT" },
    { text: "DE", value: "DE" },
    { text: "DC", value: "DC" },
    { text: "FL", value: "FL" },
    { text: "GA", value: "GA" },
    { text: "GU", value: "GU" },
    { text: "HI", value: "HI" },
    { text: "ID", value: "ID" },
    { text: "IL", value: "IL" },
    { text: "IN", value: "IN" },
    { text: "IA", value: "IA" },
    { text: "KS", value: "KS" },
    { text: "KY", value: "KY" },
    { text: "LA", value: "LA" },
    { text: "ME", value: "ME" },
    { text: "MD", value: "MD" },
    { text: "MA", value: "MA" },
    { text: "MI", value: "MI" },
    { text: "MN", value: "MN" },
    { text: "MS", value: "MS" },
    { text: "MO", value: "MO" },
    { text: "MT", value: "MT" },
    { text: "NE", value: "NE" },
    { text: "NV", value: "NV" },
    { text: "NH", value: "NH" },
    { text: "NJ", value: "NJ" },
    { text: "NM", value: "NM" },
    { text: "NY", value: "NY" },
    { text: "NC", value: "NC" },
    { text: "ND", value: "ND" },
    { text: "MP", value: "MP" },
    { text: "OH", value: "OH" },
    { text: "OK", value: "OK" },
    { text: "OR", value: "OR" },
    { text: "PA", value: "PA" },
    { text: "PR", value: "PR" },
    { text: "RI", value: "RI" },
    { text: "SC", value: "SC" },
    { text: "SD", value: "SD" },
    { text: "TN", value: "TN" },
    { text: "TX", value: "TX" },
    { text: "TT", value: "TT" },
    { text: "UT", value: "UT" },
    { text: "VT", value: "VT" },
    { text: "VA", value: "VA" },
    { text: "VI", value: "VI" },
    { text: "WA", value: "WA" },
    { text: "WV", value: "WV" },
    { text: "WI", value: "WI" },
    { text: "WY", value: "WY" },
  ];

  const emptyData = {
    homeAddress: {
      streetAddress: [""],
      country: "USA",
      city: "",
      state: "",
      postalCode: "",
    },
    phoneNumbers: [
      {
        phoneNumber: localStorage.getItem("phone"),
        phoneNumberType: "HOME",
      },
    ],
    emailAddresses: [localStorage.getItem("email")],
  };

  const [data, setData] = useState(emptyData);

  const getErrors = () => {
    if (!submitted) return {};
    const errors = {};
    if (!data.homeAddress.state) errors.state = "State is required";
    if (!data.homeAddress.city?.trim()) errors.city = "City is required";
    if (!data.homeAddress.streetAddress?.[0]?.trim()) errors.streetAddress = "Street address is required";
    if (!data.homeAddress.postalCode?.trim()) errors.postalCode = "Postal code is required";
    return errors;
  };

  const errors = {
    ...getErrors(),
    ...(backendFieldErrors.homeCity ? { city: backendFieldErrors.homeCity } : {}),
    ...(backendFieldErrors.homeState ? { state: backendFieldErrors.homeState } : {}),
    ...(backendFieldErrors.homeStreetAddress ? { streetAddress: backendFieldErrors.homeStreetAddress } : {}),
    ...(backendFieldErrors.homePostalCode ? { postalCode: backendFieldErrors.homePostalCode } : {}),
  };

  const handleChange = (field, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleNestedChange = (field, subField, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        [subField]: value,
      },
    }));
  };

  const handleArrayInsert = (field, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: [value],
    }));
  };

  const handleArrayNestedInsert = (field, subField, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        [subField]: [value],
      },
    }));
  };

  const handlePhoneNumber = (field, subField, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: [
        {
          [subField]: value,
          phoneNumberType: "HOME",
        },
      ],
    }));
  };

  const handleDate = (date, field) => {
    handleChange(field, date);
  };

  useEffect(() => {
    sendData(data);
  }, [data]);

  return (
    <>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={4} sm={4}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Country",
                name: "country",
                value: data.homeAddress.country,
                action: (e) =>
                  handleNestedChange("homeAddress", "country", e.target.value),
                tooltipBool: false,
                disabled: true,
              }}
              data={[{ text: "USA", value: "USA" }]}
            />
          </div>
        </Grid>
        <Grid item xs={4} sm={4}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "State",
                name: "state",
                value: data.homeAddress.state,
                action: (e) =>
                  handleNestedChange("homeAddress", "state", e.target.value),
                tooltipBool: false,
                required: true,
                error: errors.state,
              }}
              data={statesArray}
            ></SelectInput>
          </div>
        </Grid>
        <Grid item xs={4} sm={4}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "City",
                name: "city",
                value: data.homeAddress.city,
                action: (e) =>
                  handleNestedChange("homeAddress", "city", e.target.value),
                tooltipBool: false,
                required: true,
                error: errors.city,
              }}
            ></TextInput>
          </div>
        </Grid>
      </Grid>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={8} sm={8}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Street Address",
                name: "streetAddress",
                value: data.homeAddress.streetAddress,
                maxLength: 30,
                action: (e) =>
                  handleArrayNestedInsert(
                    "homeAddress",
                    "streetAddress",
                    e.target.value
                  ),
                tooltipBool: false,
                required: true,
                error: errors.streetAddress,
              }}
            />
          </div>
        </Grid>
        <Grid item xs={4} sm={4}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Postal Code",
                name: "postalCode",
                value: data.homeAddress.postalCode,
                action: (e) =>
                  handleNestedChange(
                    "homeAddress",
                    "postalCode",
                    e.target.value
                  ),
                tooltipBool: false,
                required: true,
                error: errors.postalCode,
              }}
            ></TextInput>
          </div>
        </Grid>
      </Grid>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={6} sm={6}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Phone Number",
                name: "phoneNuber",
                value: data.phoneNumbers[0].phoneNumber,
                disabled: true,
                action: (e) =>
                  handlePhoneNumber(
                    "phoneNumbers",
                    "phoneNumber",
                    e.target.value
                  ),
                tooltipBool: false,
              }}
            ></TextInput>
          </div>
        </Grid>
      </Grid>
    </>
  );
}

export default Contact;
